import * as Sentry from '@sentry/react';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router';
import { ErrorFallback, RouteError } from './components/ErrorFallback';
import { Layout } from './components/Layout';
import { AddDomain } from './pages/AddDomain';
import { AdminInvites, AdminTeams } from './pages/Admin';
import { ApiKeys } from './pages/ApiKeys';
import { Billing } from './pages/Billing';
import { DomainDetail } from './pages/DomainDetail';
import { Domains } from './pages/Domains';
import { EmailDetail } from './pages/EmailDetail';
import { Emails } from './pages/Emails';
import { InvoiceDetail } from './pages/InvoiceDetail';
import { LogDetail } from './pages/LogDetail';
import { Login } from './pages/Login';
import { Logs } from './pages/Logs';
import { Metrics } from './pages/Metrics';
import { SendEmail } from './pages/SendEmail';
import { Settings } from './pages/Settings';
import { WebhookDetail } from './pages/WebhookDetail';
import { Webhooks } from './pages/Webhooks';
import { initSentry } from './sentry';
import { SessionProvider } from './session';
import './styles.css';
import './icon-motion.css';

initSentry();

const router = createBrowserRouter([
  {
    path: '/',
    element: <Layout />,
    errorElement: <RouteError />,
    children: [
      { index: true, element: <Navigate to="/emails" replace /> },
      { path: 'emails', element: <Emails /> },
      { path: 'emails/new', element: <SendEmail /> },
      { path: 'emails/:id', element: <EmailDetail /> },
      { path: 'metrics', element: <Metrics /> },
      { path: 'domains', element: <Domains /> },
      { path: 'domains/new', element: <AddDomain /> },
      { path: 'domains/:id', element: <DomainDetail /> },
      { path: 'api-keys', element: <ApiKeys /> },
      { path: 'webhooks', element: <Webhooks /> },
      { path: 'webhooks/:id', element: <WebhookDetail /> },
      { path: 'logs', element: <Logs /> },
      { path: 'logs/:id', element: <LogDetail /> },
      { path: 'settings', element: <Settings /> },
      { path: 'billing', element: <Billing /> },
      { path: 'billing/invoices/:id', element: <InvoiceDetail /> },
      { path: 'admin', element: <AdminTeams /> },
      { path: 'admin/invites', element: <AdminInvites /> },
      { path: '*', element: <Navigate to="/emails" replace /> },
    ],
  },
]);

const root = document.getElementById('root');
if (!root) throw new Error('#root missing');
createRoot(root).render(
  <StrictMode>
    {/* Catches crashes outside the routes (the session and login screens); routes use RouteError. */}
    <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
      <SessionProvider anonymous={(onLogin) => <Login onLogin={onLogin} />}>
        <RouterProvider router={router} />
      </SessionProvider>
    </Sentry.ErrorBoundary>
  </StrictMode>,
);
